// src/pages/TablesPage.js
// 桌位管理 — 自 RestaurantReservationsPage 抽出成側欄獨立頁（Plan 106 §1.2）
// console 已重新定位為「訂單管理」（訂位/外帶）；桌位 CRUD 屬設定性質，移到這裡。
// 內容與原 tab 完全相同：桌位表 + 新增/編輯 dialog + 開放配桌切換 + 刪除確認。

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, Tooltip, Stack, Skeleton, useMediaQuery, Menu, MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import Layout, { loadStoredFontScale, FONT_SCALE_EVENT } from '../components/Layout';
import restaurantService from '../services/restaurantService';

const headerSx = { bgcolor: '#ddd', fontWeight: 'bold', fontSize: '14px', position: 'sticky', top: 0, zIndex: 2, borderBottom: '1px solid #ccc' };
const cellSx = { py: 1.75, fontSize: 16 };

// 手機卡片基準字級 — 與 T-022 一致,由全域「A」字級(標準/大/特大)驅動。
// 本頁是整頁式 console(m:-3 + calc(100vh)),吃不了 Layout wrapper zoom(會破版),
// 故 <Layout disableContentZoom> + 監聽 FONT_SCALE_EVENT 自行縮放卡片。
const MOBILE_CARD_FS = { 1: 20, 1.18: 22, 1.38: 24 };
const scaleToCardFs = (scale) => MOBILE_CARD_FS[scale] || 20;

const StatItem = ({ label, value, color = '#333' }) => (
  <Box sx={{ textAlign: 'center', minWidth: 60 }}>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</Typography>
    <Typography sx={{ fontSize: 11, color: '#999', mt: 0.5 }}>{label}</Typography>
  </Box>
);

const TablesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // <md → 手機/小平板卡片
  // 全域字級(navbar「A」)— 整頁式 console 自行縮放手機卡片
  const [fontScale, setFontScale] = useState(() => loadStoredFontScale());
  useEffect(() => {
    const onScale = (e) => setFontScale(typeof e?.detail === 'number' ? e.detail : loadStoredFontScale());
    window.addEventListener(FONT_SCALE_EVENT, onScale);
    return () => window.removeEventListener(FONT_SCALE_EVENT, onScale);
  }, []);
  const mobileFs = scaleToCardFs(fontScale);

  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const showSnack = (severity, message) => setSnackbar({ open: true, severity, message });

  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [tableDialogMode, setTableDialogMode] = useState('create');
  const [tableForm, setTableForm] = useState({ id: null, tableNumber: '', seats: 4, notes: '' });
  const [tableDeleteTarget, setTableDeleteTarget] = useState(null);
  // 手機卡片 ⋮ 選單（編輯/刪除低頻，收進選單）
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuTable, setRowMenuTable] = useState(null);

  const fetchTables = useCallback(async () => {
    setTablesLoading(true);
    try {
      const body = await restaurantService.getTables();
      if (body?.success && body.data) setTables(body.data.tables || []);
      else showSnack('error', body?.error || '取得桌位列表失敗');
    } catch (e) {
      showSnack('error', '取得桌位列表失敗');
    } finally {
      setTablesLoading(false);
    }
  }, []);

  useEffect(() => { fetchTables(); }, [fetchTables]);

  const openCreateTable = () => { setTableDialogMode('create'); setTableForm({ id: null, tableNumber: '', seats: 4, notes: '' }); setTableDialogOpen(true); };
  const openEditTable = (t) => { setTableDialogMode('edit'); setTableForm({ id: t.id, tableNumber: t.tableNumber, seats: t.seats, notes: t.notes || '' }); setTableDialogOpen(true); };
  const submitTable = async () => {
    const payload = {
      tableNumber: tableForm.tableNumber.trim(),
      seats: parseInt(tableForm.seats, 10),
      notes: tableForm.notes?.trim() || null,
    };
    if (!payload.tableNumber) { showSnack('error', '請輸入桌號'); return; }
    if (!Number.isInteger(payload.seats) || payload.seats < 1) { showSnack('error', '容納人數需為 >= 1 的整數'); return; }
    const body = tableDialogMode === 'create'
      ? await restaurantService.createTable(payload)
      : await restaurantService.updateTable(tableForm.id, payload);
    if (body?.success) { showSnack('success', tableDialogMode === 'create' ? '已新增桌位' : '已更新桌位'); setTableDialogOpen(false); fetchTables(); }
    else { showSnack('error', body?.error || '儲存桌位失敗'); }
  };
  const handleToggleTable = async (t) => {
    const body = await restaurantService.toggleTable(t.id);
    if (body?.success) fetchTables();
    else showSnack('error', body?.error || '切換失敗');
  };
  const confirmDeleteTable = async () => {
    const t = tableDeleteTarget; setTableDeleteTarget(null);
    if (!t) return;
    const body = await restaurantService.deleteTable(t.id);
    if (body?.success) { showSnack('success', '已刪除桌位'); fetchTables(); }
    else { showSnack('error', body?.error || '刪除失敗（可能仍有未完成訂位）'); }
  };

  const openTableCount = useMemo(() => tables.filter(t => t.isAvailable).length, [tables]);
  const openTableSeats = useMemo(() => tables.filter(t => t.isAvailable).reduce((s, t) => s + (t.seats || 0), 0), [tables]);

  // 桌號由小到大排序（數字優先，非數字排後、以字串比）— 手機卡片與桌機表格共用
  const sortedTables = useMemo(() => {
    return [...tables].sort((a, b) => {
      const na = parseInt(a.tableNumber, 10);
      const nb = parseInt(b.tableNumber, 10);
      const aNum = !Number.isNaN(na);
      const bNum = !Number.isNaN(nb);
      if (aNum && bNum && na !== nb) return na - nb;
      if (aNum !== bNum) return aNum ? -1 : 1;
      return String(a.tableNumber).localeCompare(String(b.tableNumber));
    });
  }, [tables]);

  // 手機卡片 ⋮ 選單
  const openRowMenu = (e, t) => { setRowMenuAnchor(e.currentTarget); setRowMenuTable(t); };
  const closeRowMenu = () => { setRowMenuAnchor(null); setRowMenuTable(null); };
  const menuEdit = () => { const t = rowMenuTable; closeRowMenu(); if (t) openEditTable(t); };
  const menuDelete = () => { const t = rowMenuTable; closeRowMenu(); if (t) setTableDeleteTarget(t); };

  // 手機卡片整體依全域「A」字級等比縮放（基準 fs=20，k=1/1.1/1.2）— 保持欄位對齊
  const k = mobileFs / 20;
  const cz = (px) => Math.round(px * k);

  const renderSkeletonRows = (colCount, count = 8) => (
    Array.from({ length: count }).map((_, i) => (
      <TableRow key={`sk-${i}`}>
        {Array.from({ length: colCount }).map((__, j) => (
          <TableCell key={j} sx={cellSx}><Skeleton variant="text" /></TableCell>
        ))}
      </TableRow>
    ))
  );

  // 手機/平板：桌位卡片（方案 A：表頭對齊 + 桌號藍膠囊「N桌」左右橫寫）。
  // 一列橫排：桌號 ｜ 人數 ｜ 開放配桌開關 ｜ 備註 ｜ ⋮。整體依全域「A」字級用 cz() 等比縮放。
  const renderMobileCards = () => {
    if (tablesLoading && tables.length === 0) {
      return Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={`sk-${i}`} variant="rounded" height={cz(64)} sx={{ borderRadius: '13px', mb: 1 }} />
      ));
    }
    if (!tablesLoading && tables.length === 0) {
      return (
        <Box sx={{ py: 6, textAlign: 'center', color: '#999' }}>
          <TableRestaurantIcon sx={{ fontSize: 48, color: '#ddd', mb: 1, display: 'block', mx: 'auto' }} />
          <Typography>尚無桌位，按右上「新增桌位」開始</Typography>
        </Box>
      );
    }
    const cols = `${cz(64)}px ${cz(40)}px ${cz(62)}px 1fr ${cz(36)}px`;
    const gridBase = { display: 'grid', gridTemplateColumns: cols, columnGap: `${cz(6)}px`, alignItems: 'center' };
    return (
      <>
        {/* 表頭 */}
        <Box sx={{ ...gridBase, px: `${cz(12)}px`, pt: `${cz(4)}px`, pb: `${cz(8)}px` }}>
          {['桌號', '人數', '開放配桌', '備註', ''].map((l, i) => (
            <Typography key={i} sx={{ fontSize: cz(11), fontWeight: 800, color: '#9aa7b6', letterSpacing: '.4px', lineHeight: 1 }}>{l}</Typography>
          ))}
        </Box>
        {sortedTables.map(t => (
          <Paper key={t.id}
            sx={{ mb: `${cz(9)}px`, borderRadius: '13px', boxShadow: '0 1px 3px rgba(15,43,74,0.08)',
                  borderLeft: `4px solid ${t.isAvailable ? '#4caf50' : '#9e9e9e'}` }}>
            <Box sx={{ ...gridBase, px: `${cz(12)}px`, py: `${cz(11)}px` }}>
              {/* 桌號藍膠囊：N桌 左右橫寫 */}
              <Box sx={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'baseline', gap: '1px',
                         bgcolor: '#e8f0f9', color: '#145da0', borderRadius: `${cz(9)}px`, px: `${cz(8)}px`, py: `${cz(5)}px` }}>
                <Typography component="span" sx={{ fontSize: cz(19), fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{t.tableNumber}</Typography>
                <Typography component="span" sx={{ fontSize: cz(12), fontWeight: 700 }}>桌</Typography>
              </Box>
              {/* 人數 */}
              <Typography sx={{ justifySelf: 'start', fontSize: cz(17), fontWeight: 800, color: '#0f2b4a', fontVariantNumeric: 'tabular-nums' }}>
                {t.seats}<Box component="span" sx={{ fontSize: cz(11), fontWeight: 600, color: '#6b7280' }}>人</Box>
              </Typography>
              {/* 開放配桌開關（獨佔一欄，加大，開＝綠）*/}
              <Box sx={{ justifySelf: 'start', transform: `scale(${k})`, transformOrigin: 'left center' }}>
                <Switch color="success" checked={!!t.isAvailable} onChange={() => handleToggleTable(t)} />
              </Box>
              {/* 備註 */}
              <Typography sx={{ fontSize: cz(13), color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.notes || '—'}
              </Typography>
              {/* ⋮ 編輯/刪除 */}
              <IconButton onClick={(e) => openRowMenu(e, t)} sx={{ justifySelf: 'center', color: '#90a4ae', p: `${cz(4)}px` }}>
                <MoreVertIcon sx={{ fontSize: cz(22) }} />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </>
    );
  };

  return (
    <Layout disableContentZoom>
      <Box sx={{ m: -3, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ flexShrink: 0, bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: { xs: 1.5, md: 3 }, py: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: 1 }}>
            <Box sx={{ flexShrink: 0 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, whiteSpace: 'nowrap' }}>桌位管理</Typography>
              <Typography sx={{ fontSize: 13, color: '#999' }}>桌位配置與「開放配桌」設定（開＝線上訂位／AI 會自動配這桌）</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, alignItems: 'flex-end', flexWrap: 'wrap', width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'space-between', md: 'flex-end' } }}>
              <StatItem label="總桌數" value={tables.length} />
              <StatItem label="開放配桌" value={openTableCount} color="#145da0" />
              <StatItem label="開放座位" value={openTableSeats} color="#4caf50" />
            </Box>
          </Box>
        </Box>

        {/* Action row */}
        <Box sx={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5,
          px: { xs: 1.5, md: 3 }, py: 1.5, bgcolor: '#f9f9f9', borderBottom: '1px solid #f0f0f0',
        }}>
          <Tooltip title="重新整理">
            <IconButton onClick={() => fetchTables()} disabled={tablesLoading} size="small"
              sx={{ borderRadius: '6px', bgcolor: 'rgba(20,93,160,0.06)', '&:hover': { bgcolor: 'rgba(20,93,160,0.14)' } }}>
              <RefreshIcon fontSize="small" sx={{ color: '#145da0' }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          {/* 新增桌位：手機/桌機都放在此列右上（與重新整理同排）*/}
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openCreateTable}
            sx={{ bgcolor: '#145da0', '&:hover': { bgcolor: '#0e447a' }, textTransform: 'none' }}>
            新增桌位
          </Button>
        </Box>

        {/* Content：手機卡片 / 桌機表格 */}
        <Box sx={{
          flex: 1, overflow: 'auto', px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 0 },
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
        }}>
          {isMobile ? renderMobileCards() : (
          <TableContainer component={Paper} sx={{ mt: 2, mb: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerSx}>桌號</TableCell>
                  <TableCell sx={headerSx} align="right">容納人數</TableCell>
                  <TableCell sx={headerSx}>狀態</TableCell>
                  <TableCell sx={headerSx}>備註</TableCell>
                  <TableCell sx={headerSx} align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tablesLoading && tables.length === 0 && renderSkeletonRows(5)}
                {!tablesLoading && tables.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#999' }}>
                      <TableRestaurantIcon sx={{ fontSize: 48, color: '#ddd', mb: 1, display: 'block', mx: 'auto' }} />
                      <Typography>尚無桌位，按右上「新增桌位」開始</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {sortedTables.map(t => (
                  <TableRow key={t.id} sx={{ '&:hover': { bgcolor: '#f0f4ff !important' } }}>
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{t.tableNumber}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">{t.seats}</TableCell>
                    <TableCell sx={cellSx}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Switch size="small" checked={!!t.isAvailable} onChange={() => handleToggleTable(t)} />
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.isAvailable ? '#4caf50' : '#9e9e9e' }} />
                        <Typography sx={{ fontSize: 13, color: t.isAvailable ? '#2e7d32' : '#666' }}>
                          {t.isAvailable ? '開放配桌' : '人工保留'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t.notes || '—'}
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="編輯">
                          <IconButton size="small" onClick={() => openEditTable(t)} sx={{ width: 30, height: 30, borderRadius: '6px', bgcolor: 'rgba(3,169,244,0.08)', '&:hover': { bgcolor: 'rgba(3,169,244,0.18)' } }}>
                            <EditIcon fontSize="small" sx={{ color: '#03a9f4' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="刪除">
                          <IconButton size="small" onClick={() => setTableDeleteTarget(t)} sx={{ width: 30, height: 30, borderRadius: '6px', bgcolor: 'rgba(211,47,47,0.08)', '&:hover': { bgcolor: 'rgba(211,47,47,0.18)' } }}>
                            <DeleteIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Box>
      </Box>

      {/* 手機卡片 ⋮ 選單：編輯 / 刪除 */}
      <Menu anchorEl={rowMenuAnchor} open={!!rowMenuAnchor} onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={menuEdit}>
          <EditIcon fontSize="small" sx={{ color: '#03a9f4', mr: 1 }} />編輯桌位
        </MenuItem>
        <MenuItem onClick={menuDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />刪除桌位
        </MenuItem>
      </Menu>

      <Dialog open={tableDialogOpen} onClose={() => setTableDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tableDialogMode === 'create' ? '新增桌位' : '編輯桌位'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="桌號" value={tableForm.tableNumber} onChange={(e) => setTableForm(f => ({ ...f, tableNumber: e.target.value }))} fullWidth autoFocus />
            <TextField label="容納人數" type="number" value={tableForm.seats} onChange={(e) => setTableForm(f => ({ ...f, seats: e.target.value }))} inputProps={{ min: 1 }} fullWidth />
            <TextField label="備註" value={tableForm.notes} onChange={(e) => setTableForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDialogOpen(false)}>取消</Button>
          <Button variant="contained" onClick={submitTable}>儲存</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!tableDeleteTarget} onClose={() => setTableDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>刪除桌位</DialogTitle>
        <DialogContent>
          <Typography>
            確定刪除桌位 <b>{tableDeleteTarget?.tableNumber}</b>？若該桌位仍有未完成訂位，後端會拒絕刪除。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableDeleteTarget(null)}>取消</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteTable}>刪除</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default TablesPage;
